import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Package, Users, ClipboardText, Lightbulb, WarningCircle, CurrencyDollar, Truck, Wrench, Recycle, Receipt,
  BowlFood, ArrowRight,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard, EmptyState } from "@/components/Shared";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
} from "recharts";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        overline={`Signed in as ${user?.role}`}
        title={`Floor overview, ${user?.name?.split(" ")[0]}.`}
        description="Live snapshot of inventory, supply requests, partners and improvement ideas."
      />

      <Link
        to="/fridge"
        data-testid="dashboard-fridge-tile"
        className="group mb-4 flex items-start gap-4 border-l-4 border-[#0B5C2E] bg-[#ECF7F0] p-4 transition-colors duration-150 hover:bg-[#DFF0E6] sm:mb-6 sm:p-5"
      >
        <BowlFood size={26} weight="duotone" className="mt-0.5 shrink-0 text-[#0B5C2E]" />
        <div className="min-w-0">
          <div className="font-display text-sm font-bold text-[#0B5C2E] md:text-base">
            Workplace Fridge
          </div>
          <p className="mt-1 text-sm leading-relaxed text-slate-700">
            Fresh cut fruit salad, sandwiches and snacks dropped into the shared fridge on a weekly
            schedule — see what's in today, what to eat first, and take yours.
          </p>
          <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#0B5C2E] underline">
            Open the fridge <ArrowRight size={13} className="transition-transform duration-150 group-hover:translate-x-0.5" />
          </span>
        </div>
      </Link>

      {!stats ? (
        <div className="text-sm text-slate-500" data-testid="dashboard-loading">Loading metrics…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            <StatCard testid="stat-total-items" label="Inventory items" value={stats.total_items} icon={Package} sub="materials · consumables · tools · equipment" />            <StatCard testid="stat-low-stock" label="Low stock" value={stats.low_stock_count} icon={WarningCircle} tone="danger" sub="at or below reorder level" />
            <StatCard testid="stat-inventory-value" label="Stock value" value={`$${Number(stats.inventory_value).toLocaleString()}`} icon={CurrencyDollar} tone="primary" />
            <StatCard testid="stat-pending-requests" label="Pending requests" value={stats.pending_requests} icon={ClipboardText} sub={`${stats.total_requests} total`} />
            <StatCard testid="stat-vendors" label="Vendors & sellers" value={stats.vendors} icon={Users} />
            <StatCard testid="stat-buyers" label="Buyers" value={stats.buyers} icon={Users} />
            <StatCard testid="stat-ideas" label="Floor ideas" value={stats.total_ideas} icon={Lightbulb} sub={`${stats.implemented_ideas} implemented`} tone="success" />
            <StatCard testid="stat-in-transit" label="In transit" value={stats.in_transit ?? 0} icon={Truck} tone="primary" sub="shipments on the way" />
            <StatCard testid="stat-open-orders" label="Open POs" value={stats.open_orders ?? 0} icon={Receipt} tone="primary" sub="draft, sent or partial" />
            <StatCard testid="stat-tools-issued" label="Tools issued" value={stats.tools_issued ?? 0} icon={Wrench} sub={`${stats.tools_total ?? 0} in the crib`} />
            <StatCard testid="stat-tools-overdue" label="Tools overdue" value={stats.tools_overdue ?? 0} icon={WarningCircle} tone="danger" sub="past due-back date" />
            <StatCard testid="stat-open-rework" label="Open rework" value={stats.open_rework ?? 0} icon={Wrench} sub="parts being redone" />
            <StatCard testid="stat-scrap-month" label="Scrap this month" value={`$${Number(stats.scrap_value_month ?? 0).toLocaleString()}`} icon={Recycle} tone="danger" sub="material value lost" />
            <StatCard testid="stat-feedback" label="Open feedback" value={stats.open_feedback} icon={Users} />
          </div>

          {stats.overdue_tools?.length > 0 && (
            <div
              data-testid="overdue-tools-alert"
              className="mt-4 border-l-4 border-[#FF3B30] bg-[#FFECEB] p-4 sm:mt-6"
            >
              <div className="flex items-start gap-2.5">
                <WarningCircle size={18} className="mt-0.5 shrink-0 text-[#B3241B]" />
                <div className="min-w-0">
                  <div className="font-display text-sm font-bold text-[#B3241B]">
                    {stats.overdue_tools.length} tool{stats.overdue_tools.length > 1 ? "s" : ""} overdue
                  </div>
                  <ul className="mt-1.5 space-y-1">
                    {stats.overdue_tools.map((t) => (
                      <li key={t.name} className="text-xs text-[#B3241B]">
                        <span className="font-semibold">{t.name}</span> — {t.issued_to_name}, due {t.due_back}
                      </li>
                    ))}
                  </ul>
                  <Link to="/tools" className="tap-safe mt-2 inline-flex items-center text-xs font-semibold text-[#B3241B] underline">
                    Open tool crib
                  </Link>
                </div>
              </div>
            </div>
          )}

          <div className="mt-4 grid grid-cols-1 gap-4 sm:mt-6 sm:gap-6 lg:grid-cols-5">
            <div className="border border-border bg-white p-4 sm:p-5 lg:col-span-3" data-testid="category-chart">
              <div className="overline mb-4 text-slate-500">Items by category</div>
              <div className="h-52 min-w-0 sm:h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats.by_category}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748B" }} />
                    <YAxis tick={{ fontSize: 12, fill: "#64748B" }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#002FA7" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="border border-border bg-white p-4 sm:p-5 lg:col-span-2" data-testid="low-stock-panel">
              <div className="mb-4 flex items-center justify-between">
                <div className="overline text-slate-500">Reorder now</div>
                <Link to="/inventory" className="tap-safe inline-flex items-center text-xs font-semibold text-[#002FA7] hover:underline">
                  Open inventory
                </Link>
              </div>
              {stats.low_stock_items.length === 0 ? (
                <EmptyState testid="no-low-stock" title="All stocked up" description="Nothing is below its reorder level." />
              ) : (
                <ul className="divide-y divide-border">
                  {stats.low_stock_items.map((i) => (
                    <li key={i.name} className="flex items-center justify-between py-2.5">
                      <span className="text-sm font-medium text-[#0F172A]">{i.name}</span>
                      <span className="font-mono text-xs text-[#FF3B30]">
                        {i.quantity} / {i.reorder_level} {i.unit}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
